import CompanyService, { DataType } from "../../../../services/CompanyService";
import FilterService, {
  FilterCollection,
  FilterEntity,
  FilterOption
} from "../../../../services/FilterService";
import { PickByValue } from "../../../../utils/Utils";
import AdvancedFilters, {
  AdvancedFiltersContext
} from "../../../shared/components/AdvancedFilters";
import Dropdown from "../../../shared/components/Dropdown";
import { CheckBoxEditor } from "../../../shared/components/Editors";
import { ImportTemplateListResponse } from "../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../services/TranslationService";
import Alarm from "./Components/Alarm";
import DateFormat from "./Components/DateFormat";
import Dictionaries from "./Components/Dictionaries";
import EditItem from "./Components/EditItem";
import ExcelOptions from "./Components/ExcelOptions";
import PrecedingProcesses from "./Components/PrecedingProcesses";
import SeparatorWithFixedColumns from "./Components/SeparatorWithFixedColumns";
import UploadTemplate from "./Components/UploadTemplate";
import ImportTemplateEditBase, {
  ImportTemplateComponentProps
} from "./ImportTemplateEditBase";
import ImportTemplateService from "../../../../services/ImportTemplateService";
import ModalService from "../../../shared/bootstrap/modal/Modal";
import ProcessService from "../../../../services/ProcessService";
import Frequency from "./Components/Frecuency";
import AdvancedFilterService from "../../../../services/AdvancedFilterService";
import { useContext, useEffect } from "react";
import FloatingPanelService from "../../../shared/FloatingPanel";
import ImportTemplateEditClientExportTemplate from "./ImportTemplateEditClientExportTemplate";

const ImportTemplateEditInvoiceAutomatic = ({
  data,
  reload,
  importsList = undefined,
  entityId = undefined
}: {
  data?: ImportTemplateListResponse.Item;
  reload: () => void;
  importsList?: ImportTemplateListResponse;
  entityId?: number;
}) => {
  const requiredIds = ["contact", "referenceNumber"];
  const advancedFiltersContext = useContext(AdvancedFiltersContext);

  useEffect(() => {
    advancedFiltersContext.setShow(true);
  }, [advancedFiltersContext]);

  const Header = () => {
    return (
      <div className="mx-4 mt-4 d-flex align-items-center justify-content-between">
        <h5>
          {
            TranslationService.translate
              .ImportAutomaticInvoiceUpdateConfiguration
          }{" "}
        </h5>
        <div className="col-auto align-self-end">
          <button
            className="btn btn-link"
            onClick={() =>
              FloatingPanelService.showPanel({
                title: TranslationService.translate.ImportTemplates,
                children: (
                  <ImportTemplateEditClientExportTemplate
                    reload={() => undefined}
                    data={data}
                    entityId={entityId}
                  />
                )
              })
            }
          >
            {TranslationService.translate.SelectColumns}
          </button>
          <button
            className="btn btn-link"
            onClick={() =>
              ProcessService.showResultToast(
                ImportTemplateService.exportIoList(data!.ImportTemplateID)
              )
            }
          >
            {TranslationService.translate.Export}
          </button>
        </div>
      </div>
    );
  };

  const RunNow = () => {
    ModalService.showDefaultModal({
      acceptButtonLabel: TranslationService.translate.RunNow,
      message:
        TranslationService.translate
          .AreYouSureYouWantToAutomaticallyUpdateInvoicesNow,
      onAcceptClick: () =>
        ProcessService.showResultToast(ImportTemplateService.runIOUpdate())
    });
  };
  return (
    <ImportTemplateEditBase
      entityId={entityId}
      Header={Header}
      AdvancedOptions={AdvancedOptions}
      reload={reload}
      requiredFieldIds={requiredIds}
      data={data}
      runNow={RunNow}
      showName
    />
  );
};

const AdvancedOptions = ({
  data,
  updateModel,
  model,
  setModel,
  importsList
}: ImportTemplateComponentProps) => {
  const updateNumberModel =
    <
      T extends keyof PickByValue<
        ImportTemplateListResponse.Item,
        number | undefined
      >
    >(
      key: T
    ) =>
    (value: number) => {
      setModel((x) => ({ ...x, [key]: value }));
    };
  const applyFilter = (filters: string[] | undefined) =>
    setModel((x) => ({
      ...x,
      IOFilter: AdvancedFilterService.filterStringsToQueryString(filters)
    }));
  const filterOptions: FilterOption = JSON.parse(
    JSON.stringify(FilterService.GetFiltersForEntity(FilterEntity.Invoice))
  );
  filterOptions.definitions
    .filter((x) => x.Type === DataType.Date)
    .forEach((x) => (x.Type = DataType.DayCompare));

  return (
    <div>
      <Frequency
        updateModel={updateModel}
        data={data}
        setModel={setModel}
        model={model}
      />
      <hr className="my-5" />
      <small className="text-muted mb-3">
        {TranslationService.translate.IfYouLeaveFormatBlank}
      </small>
      <DateFormat
        defaultValue={data?.DateFormat}
        updateModel={updateModel("DateFormat")}
        label={TranslationService.translate.IssueDateFormat}
      />
      <DateFormat
        defaultValue={data?.DueDateFormat}
        updateModel={updateModel("DueDateFormat")}
        label={TranslationService.translate.DueDateFormat}
      />
      <CheckBoxEditor
        message={
          TranslationService.translate
            .UpdateExistingInvoiceDataWhenSynchronizing
        }
        onChange={updateModel("UpdateIOs")}
        defaultValue={data?.UpdateIOs}
      />
      {CompanyService.getIsAdmin() && (
        <div
          className="py-2 mb-3"
          style={{ paddingLeft: 5, marginLeft: -5, backgroundColor: "#f5eebe" }}
        >
          <CheckBoxEditor
            message={"Actualizar datos de facturas pagadas"}
            onChange={updateModel("IncludePaid")}
            defaultValue={data?.IncludePaid}
          />
          <CheckBoxEditor
            message={"Procesar facturas con monto 0 en el archivo"}
            onChange={updateModel("processZeroIOs")}
            defaultValue={data?.processZeroIOs}
          />
        </div>
      )}
      <EditItem label={TranslationService.translate.WhenInvoiceRepeated}>
        <Dropdown
          onChange={updateNumberModel("OnRepeat")}
          defaultValue={data?.OnRepeat}
          items={[
            { value: 0, text: TranslationService.translate.DiscardFile },
            {
              value: 1,
              text: TranslationService.translate.DiscardRepeatedInvoices
            },
            {
              value: 2,
              text: TranslationService.translate.SumRepeatedInvoicesAmount
            },
            { value: 3, text: TranslationService.translate.TakeFirst },
            { value: 4, text: TranslationService.translate.TakeLast }
          ]}
        />
      </EditItem>
      {CompanyService.canEditTemplate() && (
        <UploadTemplate
          formatsAllowed=".xlsx,.csv"
          model={model}
          updateModel={updateModel}
        />
      )}
      <SeparatorWithFixedColumns
        updateModel={updateModel}
        data={data}
        model={model}
      />
      <ExcelOptions data={data} updateModel={updateModel} />
      {CompanyService.getSetting("allowimportdictionary") && (
        <>
          <hr className="my-5" />
          <Dictionaries data={data} model={model} setModel={setModel} />
        </>
      )}
      {CompanyService.getSetting("partialiosync") && (
        <>
          <hr className="my-5" />
          <h5 className="mb-3">
            {TranslationService.translate.FilterInvoices}
          </h5>
          <AdvancedFilters
            page={FilterCollection.Invoice}
            filterOptions={[filterOptions]}
            onFilterApply={applyFilter}
            defaultValue={data?.IOFilter}
            allowClosing={false}
          />
        </>
      )}
      {CompanyService.getSetting("allowimportpredecessor") && (
        <>
          <hr className="my-5" />
          <PrecedingProcesses
            data={data}
            model={model}
            setModel={setModel}
            importsList={importsList}
          />
        </>
      )}
      <hr className="my-5" />
      <Alarm
        data={data}
        updateModel={updateModel}
        setModel={setModel}
        model={model}
      />
    </div>
  );
};

export default ImportTemplateEditInvoiceAutomatic;
